import React, { useRef, useEffect, useState } from 'react';

// Real Cordyceps militaris turntable video whose rotation is driven by scroll.
// As the visitor scrolls through the hero, the mushroom cluster turns. Falls back
// to an autoplay loop on touch / reduced-motion devices, and to a still poster if
// the video can't load.
//
// Scrubbing is driven off the `seeked` event rather than a per-frame easing loop:
// the source MP4 has sparse keyframes, so we issue exactly one seek at a time and
// only chase a new target once the previous seek has resolved. This avoids the
// seek-thrash that leaves currentTime pinned near the start.
const CordycepsHeroVideo = () => {
  const videoRef = useRef(null);
  const wrapRef = useRef(null);
  const targetRef = useRef(0);
  const seekingRef = useRef(false);
  const [failed, setFailed] = useState(false);
  const [ready, setReady] = useState(false);

  // Devices that should just loop the video instead of scrubbing it.
  const preferLoop =
    typeof window !== 'undefined' &&
    (window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      window.matchMedia('(hover: none)').matches);

  useEffect(() => {
    if (preferLoop || !ready) return undefined;
    const video = videoRef.current;
    const wrap = wrapRef.current;
    if (!video || !wrap || !video.duration) return undefined;

    const maxT = video.duration - 0.05;

    const computeTarget = () => {
      const rect = wrap.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      // 0 as the hero enters from the bottom, 1 once it has fully passed the top.
      const progress = Math.min(1, Math.max(0, (vh - rect.top) / (rect.height + vh)));
      targetRef.current = progress * maxT;
    };

    const seekToTarget = () => {
      if (seekingRef.current) return;
      const t = targetRef.current;
      if (Math.abs(t - video.currentTime) < 0.02) return;
      seekingRef.current = true;
      try {
        video.currentTime = t;
      } catch (e) {
        seekingRef.current = false;
      }
    };

    const onSeeked = () => {
      seekingRef.current = false;
      seekToTarget();
    };

    const onScroll = () => {
      computeTarget();
      seekToTarget();
    };

    video.addEventListener('seeked', onSeeked);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    computeTarget();
    seekToTarget();

    return () => {
      video.removeEventListener('seeked', onSeeked);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [preferLoop, ready]);

  const handleLoaded = () => {
    setReady(true);
    const video = videoRef.current;
    if (video && preferLoop) {
      const p = video.play();
      if (p && p.catch) p.catch(() => {});
    }
  };

  if (failed) {
    return (
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          minHeight: '560px',
          borderRadius: '16px',
          overflow: 'hidden',
          border: '1px solid #1c1c1c',
          background: '#020202',
        }}
      >
        <img
          src="/media/hero-poster.jpg"
          alt="Cordyceps militaris"
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </div>
    );
  }

  return (
    <div
      ref={wrapRef}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        minHeight: '560px',
        borderRadius: '16px',
        overflow: 'hidden',
        border: '1px solid #1c1c1c',
        background: 'radial-gradient(circle at 50% 40%, #0d0d0d 0%, #000000 100%)',
        boxShadow: '0 40px 120px rgba(0,0,0,0.6)',
      }}
    >
      <video
        ref={videoRef}
        src="/media/hero.mp4"
        poster="/media/hero-poster.jpg"
        muted
        playsInline
        loop={preferLoop}
        autoPlay={preferLoop}
        preload="auto"
        onLoadedMetadata={handleLoaded}
        onError={() => setFailed(true)}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
          opacity: ready ? 1 : 0,
          transition: 'opacity 0.6s ease',
        }}
      />

      {/* Subtle vignette so the cluster reads against the page */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background:
            'radial-gradient(circle at 50% 45%, transparent 55%, rgba(0,0,0,0.55) 100%)',
        }}
      />

      <div
        style={{
          position: 'absolute',
          top: '18px',
          right: '18px',
          background: 'rgba(10,10,10,0.7)',
          backdropFilter: 'blur(6px)',
          border: '1px solid #2e2e2e',
          color: '#ffffff',
          padding: '6px 12px',
          borderRadius: '4px',
          fontSize: '0.62rem',
          fontWeight: 700,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
        }}
      >
        {preferLoop ? 'Live Cordyceps militaris' : 'Scroll to rotate · Cordyceps militaris'}
      </div>
    </div>
  );
};

export default CordycepsHeroVideo;
